import React, { useCallback, useMemo } from "react";
import { ScrollView } from "react-native";
import { LazyQueryNetworkBoundrary } from "../../../../../../components/mobile/QueryNetworkBoundrary";
import { useIntlTokens } from "../../../../../../components/mobile/useIntlTokens";
import { getWidgetAddWidgetSizeWithinBounds } from "../../utils/useWidgetAddWidgetSizeState";
import { WACategoryScreenProps } from "../../WANavigation";
import { WACategoryWidgetContainer } from "../../WACategoryWidgetContainer";
import { WidgetTaskSingleMockPreview } from "./WidgetTaskSingleMockPreview";
import { useWidgetTaskSingleMockPreviewQueryLoader } from "./useWidgetTaskSingleMockPreviewQueryLoader";
import { WidgetTaskCollectionMockPreview } from "./WidgetTaskCollectionMockPreview";
import { useWidgetTaskCollectionPreviewQueryLoader } from "./useWidgetTaskCollectionMockPreviewQueryLoader";

import { taskSingleWidgetSizes } from "../../utils/widgetSizes/taskSingleWidgetSizes";
import { useWACategoryLayout } from "../../utils/useWACategoryLayout";
import { WACategoryItemSeparator } from "../../WACategoryItemSeparator";
import { FormattedMessage } from "react-intl";

type Props = WACategoryScreenProps<"WACategoryChecklists">;

export function WACategoryChecklists(props: Props) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => (
        <InnerWACategoryChecklists {...props} fetchKey={fetchKey} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerWACategoryChecklists(props: Props & { fetchKey: string }) {
  const [tasksSize, tasksHasSizeWithinBounds] =
    getWidgetAddWidgetSizeWithinBounds({
      widgetSizes: [{ w: 2, h: 1 }],
      maxSize: props.route.params.maxSize,
    });
  const [taskSingleSize, taskSingleHasSizeWithinBounds] =
    getWidgetAddWidgetSizeWithinBounds({
      widgetSizes: taskSingleWidgetSizes,
      maxSize: props.route.params.maxSize,
    });
  const intl_tokens = useIntlTokens();

  const navigationParams = useMemo(
    () =>
      ({
        defaultTeams: props.route.params.defaultTeams,
        position: props.route.params.position,
        storecastId: props.route.params.storecastId,
        unitSize: props.route.params.unitSize,
        maxSize: props.route.params.maxSize,
      }) as const,
    [
      props.route.params.defaultTeams,
      props.route.params.maxSize,
      props.route.params.position,
      props.route.params.storecastId,
      props.route.params.unitSize,
    ]
  );

  const onAddTaskCollection = useCallback(() => {
    props.navigation.navigateDeprecated(
      "WidgetAddTaskCollection",
      navigationParams
    );
  }, [navigationParams, props.navigation]);
  const onAddTaskSingle = useCallback(() => {
    props.navigation.navigateDeprecated(
      "WidgetAddTaskSingle",
      navigationParams
    );
  }, [navigationParams, props.navigation]);

  const queryRef = useWidgetTaskSingleMockPreviewQueryLoader(
    props.route.params.storecastId
  );

  const taskCollectionQueryRef = useWidgetTaskCollectionPreviewQueryLoader(
    props.route.params.storecastId
  );
  const contentContainerStyle = useWACategoryLayout();
  return (
    <ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={contentContainerStyle}
    >
      <WACategoryWidgetContainer
        hasSizeWithinBounds={tasksHasSizeWithinBounds}
        size={tasksSize}
        title={
          <FormattedMessage
            defaultMessage="Checkpoints"
            description="WATaskCollection title"
          />
        }
        description={
          <FormattedMessage
            defaultMessage="Show status for a set of tasks"
            description="WATaskCollection description"
          />
        }
        unitSize={props.route.params.unitSize}
        onAdd={onAddTaskCollection}
        addTitle={intl_tokens.SelectTasks}
      >
        {taskCollectionQueryRef ? (
          <WidgetTaskCollectionMockPreview
            queryRef={taskCollectionQueryRef}
            storecastId={props.route.params.storecastId}
          />
        ) : undefined}
      </WACategoryWidgetContainer>
      <WACategoryItemSeparator />
      <WACategoryWidgetContainer
        hasSizeWithinBounds={taskSingleHasSizeWithinBounds}
        size={taskSingleSize}
        title={intl_tokens.Task}
        description={
          <FormattedMessage
            defaultMessage="Show the latest results for a task"
            description="WATaskSingle description"
          />
        }
        unitSize={props.route.params.unitSize}
        onAdd={onAddTaskSingle}
        addTitle={intl_tokens.SelectTask}
      >
        {queryRef ? (
          <WidgetTaskSingleMockPreview
            queryRef={queryRef}
            clientForceSize={
              props.route.params.maxSize.w === 2 ? "TWO_X_ONE" : "ONE_X_ONE"
            }
            storecastId={props.route.params.storecastId}
          />
        ) : undefined}
      </WACategoryWidgetContainer>
    </ScrollView>
  );
}
